import { useEffect, useState } from 'react';

export const READY_STATE = {
  loading: 1,
  interactive: 2,
  complete: 3,
}

function getReadyStateInt(state) {
  return (typeof state === 'number') ? state : READY_STATE[state];
}

/**
 * Hook for delaying effects until the document reaches the desired loading state
 * @param {Number|String} [targetState=2] - one of 2/'interactive' (`DOMContentLoaded` event)
 *   or 3/'complete' (`load` event)
 * @returns {Boolean}
 */
export function usePageReadyState(targetState = READY_STATE.interactive) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const targetStateInt = getReadyStateInt(targetState);

    function readyStateChangeHandler() {
      if (getReadyStateInt(document.readyState) >= targetStateInt) {
        document.removeEventListener('readystatechange', readyStateChangeHandler);
        setIsReady(true);
        return true;
      }
      return false;
    }

    if (!readyStateChangeHandler()) {
      document.addEventListener('readystatechange', readyStateChangeHandler);
    }

    return () => document.removeEventListener('readystatechange', readyStateChangeHandler);
  }, []);

  return isReady;
}

/**
 * Hook for delaying effects until the document is interactive (`DOMContentLoaded` event)
 * @returns {Boolean}
 */
export function useDOMContentLoaded() {
  return usePageReadyState(READY_STATE.interactive);
}

/**
 * Hook for delaying effects until the document has completed loading (`load` event)
 * @returns {Boolean}
 */
export function usePageLoad() {
  return usePageReadyState(READY_STATE.complete);
}
